import { FeatureFlagSet } from "client/defs";

import { merchantAccountInitialState } from "client/store/constants";

import * as fieldSets from "./fieldsets";
import {
  MerchantAccount,
  MerchantError,
  MerchantFieldMapper,
  MerchantFieldSetValueMap,
} from "./types";

export class Merchant {
  // Fieldset Collections
  public cashPay: fieldSets.CashPayFieldSet;
  public companyInformation: fieldSets.CompanyInformationFieldSet;
  public customerSupport: fieldSets.CustomerSupportFieldSet;
  public lead: fieldSets.LeadFieldSet;
  public locations: fieldSets.StoreLocation[] = [];
  public owners: fieldSets.BusinessOwner[] = [];
  public primaryBank: fieldSets.BankFieldSet;
  public secondaryBank: fieldSets.BankFieldSet;
  public storeDetails: fieldSets.StoreDetailsFieldSet;

  // values that some fields of type FileUpload "required" attributes relies on
  public merchantAccount: MerchantAccount;

  private static instance: Merchant;

  public static flagsInstance: Partial<FeatureFlagSet>;

  public static getInstance(): Merchant {
    if (!Merchant.instance) {
      Merchant.instance = new Merchant();
    }
    return Merchant.instance;
  }

  constructor() {
    this.merchantAccount = merchantAccountInitialState;

    this.cashPay = new fieldSets.CashPayFieldSet(this);
    this.companyInformation = new fieldSets.CompanyInformationFieldSet(this);
    this.customerSupport = new fieldSets.CustomerSupportFieldSet(this);
    this.lead = new fieldSets.LeadFieldSet(this);
    this.locations = [];
    this.owners = [
      new fieldSets.BusinessOwner(this, {
        controlValues: {
          requireCertifiedId:
            this.merchantAccount.ownerControls[0]?.requireCertifiedId ?? false,
          requireId: this.merchantAccount.ownerControls[0]?.requireId ?? false,
        },
        ownerIndex: 0,
      }),
    ];
    this.primaryBank = new fieldSets.BankFieldSet(this, "PrimaryBankFieldSet");
    this.secondaryBank = new fieldSets.BankFieldSet(
      this,
      "SecondaryBankFieldSet"
    );
    this.storeDetails = new fieldSets.StoreDetailsFieldSet(this);
  }

  /**
   * This will eventually be refactored out to a collections class?
   */
  addAdditionalStoreLocation(): void {
    this.locations.push(
      new fieldSets.StoreLocation(this, {
        locationIndex: this.locations.length,
      })
    );
  }

  /**
   * This will eventually be refactored out to a collections class?
   */
  removeStoreLocation(index?: number): void {
    if (this.locations.length) {
      if (index !== undefined && this.locations[index]) {
        this.locations.splice(index, 1);
      } else {
        this.locations.splice(-1);
      }
    }
    this.locations.map((location, idx) => {
      location.locationIndex = idx;
    });
  }

  /**
   * This will eventually be refactored out to a collections class?
   */
  addAdditionalBusinessOwner(): void {
    const numberOfOwners = this.owners.length;
    if (numberOfOwners < 5) {
      this.owners.push(
        new fieldSets.BusinessOwner(this, {
          controlValues: {
            requireCertifiedId:
              this.merchantAccount.ownerControls[numberOfOwners - 1]
                ?.requireCertifiedId ?? false,
            requireId:
              this.merchantAccount.ownerControls[numberOfOwners - 1]
                ?.requireId ?? false,
          },
          ownerIndex: numberOfOwners,
        })
      );
    }
  }

  /**
   * This will eventually be refactored out to a collections class?
   */
  removeBusinessOwner(index?: number): void {
    const numberOfOwners = this.owners.length;
    if (numberOfOwners > 1) {
      if (index !== undefined && this.owners[index]) {
        this.owners.splice(index, 1);
      } else {
        this.owners.splice(-1);
      }
    }
    this.owners.map((owner, idx) => {
      owner.ownerIndex = idx;
    });
  }

  toFieldValueMap(): MerchantFieldSetValueMap {
    return {
      cashPay: this.cashPay.toFieldValueMap(),
      companyInformation: this.companyInformation.toFieldValueMap(),
      customerSupport: this.customerSupport.toFieldValueMap(),
      lead: this.lead.toFieldValueMap(),
      locations: this.locations.map((location) => location.toFieldValueMap()),
      owners: this.owners.map((owner) => owner.toFieldValueMap()),
      primaryBank: this.primaryBank.toFieldValueMap(),
      secondaryBank: this.secondaryBank.toFieldValueMap(),
      storeDetails: this.storeDetails.toFieldValueMap(),
    };
  }

  getTestValuesForMerchant() {
    return {
      cashPay: this.cashPay.getTestValuesForFieldSet(),
      companyInformation: this.companyInformation.getTestValuesForFieldSet(),
      customerSupport: this.customerSupport.getTestValuesForFieldSet(),
      lead: this.lead.getTestValuesForFieldSet(),
      locations: this.locations.map((location) =>
        location.getTestValuesForFieldSet()
      ),
      owners: this.owners.map((owner) => owner.getTestValuesForFieldSet()),
      primaryBank: this.primaryBank.getTestValuesForFieldSet(),
      secondaryBank: this.secondaryBank.getTestValuesForFieldSet(),
      storeDetails: this.storeDetails.getTestValuesForFieldSet(),
    };
  }

  toJSON(): string {
    return JSON.stringify(this.toFieldValueMap());
  }

  /**
   * Probably going away? Leaving for now for demonstrative purposes
   */
  validateAll(stringify = true): void {
    const validationErrors: MerchantFieldMapper<MerchantError> = {
      locations: [],
      owners: [],
    };
    let errorCount = 0;
    const fieldSets = [
      this.cashPay,
      this.companyInformation,
      this.customerSupport,
      this.lead,
      this.primaryBank,
      this.secondaryBank,
      this.storeDetails,
    ];

    fieldSets.map((fieldSet) => {
      try {
        fieldSet.validate({ stringify });
      } catch (ex) {
        errorCount++;
        validationErrors[
          fieldSet.fieldSetName as keyof MerchantFieldMapper<MerchantError>
        ] = ex;
      }
    });

    this.locations.map((l) => {
      try {
        l.validate({ stringify });
      } catch (ex) {
        errorCount++;
        validationErrors.locations?.push(ex);
      }
    });
    this.owners.map((o) => {
      try {
        o.validate({ stringify });
      } catch (ex) {
        errorCount++;
        validationErrors.owners?.push(ex);
      }
    });

    if (errorCount) {
      throw validationErrors;
    }
  }
}
